import axios from 'axios';

class ExternalApiService {
  constructor() {
    const baseURL = process.env.REACT_APP_API_URL;
    if (!baseURL) {
      throw new Error('REACT_APP_API_URL is not set.');
    }

    this.keycloak = null;
    this.tenant = null;

    this.api = axios.create({
      baseURL,
      headers: {} // keep empty; we’ll set per request
    });

    this.api.interceptors.request.use(async (config) => {
      // Always apply the latest tenant
      if (this.tenant) {
        // config.headers may be AxiosHeaders or plain object; normalize
        console.log("requesting with tenant: ", this.tenant)
        config.headers = config.headers || {};
        config.headers['X-Tenant-Domain'] = this.tenant;
      }

      // Keycloak token (if present and authenticated)
      if (this.keycloak?.authenticated) {
        console.log("Keycloak still authenticated");
        try {
          // updateToken resolves true/false; it can also throw
          console.log("updating token");
          // await this.keycloak.updateToken(30);
        } catch (e) {
          // Kick off login and surface the failure to the caller
          console.log("token update failed");
          this.keycloak.login({ redirectUri: window.location.href });
          return Promise.reject(e);
        }

        if (this.keycloak.token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${this.keycloak.token}`;
          console.log("token present: ", Boolean(this.keycloak.token))
        } else {
          console.log("sending unauthenticated request");
        }
      } else {
        console.log("not authenticated");
      }

      return config;
    });

    this.api.interceptors.response.use(
      (res) => res,
      async (error) => {
        const cfg = error?.config || {};
        const status = error?.response?.status;

        // Only try refresh+retry on 401 once
        if (this.keycloak && status === 401 && !cfg.__isRetryRequest) {
          try {
            await this.keycloak.updateToken(10);
            if (!this.keycloak.token) throw new Error('No token after refresh');

            cfg.__isRetryRequest = true;
            cfg.headers = cfg.headers || {};
            cfg.headers.Authorization = `Bearer ${this.keycloak.token}`;
            return this.api.request(cfg);
          } catch (e) {
            // If refresh fails or token is missing, send user to login
            this.keycloak.login({ redirectUri: window.location.href });
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // --- Setup helpers ---

  setup(tenant) {
    // Keep for back-compat; prefer setTenant
    this.setTenant(tenant);
  }

  setKeycloakClient(keycloakInstance) {
    this.keycloak = keycloakInstance;
  }

  setTenant(tenantDomain) {
    this.tenant = tenantDomain || null;
  }

  setBearerToken(token) {
    // Useful for service-to-service or manual override
    if (!this.api) throw new Error('API client not initialized.');
    if (token) {
      this.api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common.Authorization;
    }
  }

  // Good for debugging what will actually be sent
  getDefaultHeaders() {
    return this.api?.defaults?.headers;
  }

  // --- API methods ---

  _ensureReady() {
    if (!this.api) throw new Error('API client not initialized.');
  }

  async fetchTemplates() {
    this._ensureReady();
    // Note: default headers are under defaults.headers
    // console.debug('defaults', this.api.defaults.headers);
    const res = await this.api.get('/templates');
    return res.data;
  }

  async fetchTemplateById(id) {
    this._ensureReady();
    const res = await this.api.get(`/templates/${id}`);
    return res.data;
  }

  async createTemplate(data) {
    this._ensureReady();
    const res = await this.api.post('/templates', data);
    return res.data;
  }

  async updateTemplate(id, data) {
    this._ensureReady();
    const res = await this.api.put(`/templates/${id}`, data);
    return res.data;
  }

  async deleteTemplate(id) {
    this._ensureReady();
    const res = await this.api.delete(`/templates/${id}`);
    return res.data;
  }

  async fetchMessages(page, limit) {
    this._ensureReady();
    const res = await this.api.get(`/messages?page=${page}&limit=${limit}`);
    return res.data;
  }

  async fetchEmails(page, limit) {
    this._ensureReady();
    const res = await this.api.get(`/emails?page=${page}&limit=${limit}`);
    return res.data;
  }

  async fetchMessageById(id) {
    this._ensureReady();
    const res = await this.api.get(`/messages/${id}`);
    return res.data;
  }

  async createMessage(data) {
    this._ensureReady();
    const res = await this.api.post('/messages', data);
    return res.data;
  }

  async updateMessage(id, data) {
    this._ensureReady();
    const res = await this.api.put(`/messages/${id}`, data);
    return res.data;
  }

  async deleteMessage(id) {
    this._ensureReady();
    const res = await this.api.delete(`/messages/${id}`);
    return res.data;
  }

  async sendMessage(id) {
    this._ensureReady();
    const res = await this.api.post('/messages/send', { id });
    return res.data;
  }

  async sendEmail(data) {
    this._ensureReady();
    const res = await this.api.post('/emails/send', data);
    return res.data;
  }

  async logout() {
    this._ensureReady();
    const res = await this.api.post('/logout');
    return res.data;
  }

  async sendBulkMessages(templateId, csvFile) {
    this._ensureReady();
    const formData = new FormData();
    formData.append('template_id', templateId);
    formData.append('csv_file', csvFile);
    // Don’t set Content-Type—let the browser add boundary
    const res = await this.api.post('/messages/send-bulk', formData);
    return res.data;
  }

  async sendBulkEmails(templateId, csvFile) {
    this._ensureReady();
    const formData = new FormData();
    formData.append('template_id', templateId);
    formData.append('csv_file', csvFile);
    const res = await this.api.post('/emails/send-bulk', formData);
    return res.data;
  }

  async previewTemplate(selectedTemplateId, currentData) {
    this._ensureReady();
    const res = await this.api.post(`/templates/${selectedTemplateId}/preview`, currentData);
    return res.data;
  }

  async fetchUsages() {
    this._ensureReady();
    const res = await this.api.get('/usages');
    return res.data;
  }

  /**
   * Create a payment order on the backend and get { order_id, credits, status, custom_token }.
   *
   * Usage (back-compat):
   *   await apiService.createPaymentOrder(credits, amountCents, 'JMD')
   *
   * Enhanced usage (with idempotency):
   *   await apiService.createPaymentOrder({ credits, amountCents, currency: 'JMD', idempotencyKey: uuidv4() })
   */
  async createPaymentOrder(arg1, arg2, arg3) {
    this._ensureReady();

    let credits, amountCents, currency = 'JMD', idempotencyKey;

    if (typeof arg1 === 'object' && arg1 !== null) {
      ({ credits, amountCents, currency = 'JMD', idempotencyKey } = arg1);
    } else {
      credits = arg1;
      amountCents = arg2;
      currency = arg3 ?? 'JMD';
    }

    const c = Number(credits);
    const amt = Math.round(Number(amountCents));

    if (!Number.isInteger(c) || c < 1) {
      throw new Error('Invalid credits: must be a positive integer.');
    }

    if (!Number.isFinite(amt) || amt < 1) {
      throw new Error('Invalid amount: must be a positive integer (cents).');
    }

    const cur = String(currency || 'JMD').toUpperCase();
    const payload = { credits: c, amount: amt, currency: cur };
    const headers = {};
    if (idempotencyKey) headers['X-Idempotency-Key'] = String(idempotencyKey);

    try {
      const res = await this.api.post('/payment/create', payload, { headers });
      return res.data;
    } catch (error) {
      const resp = error?.response;
      const data = resp?.data;

      const validationMsgs = data?.errors
        ? Object.entries(data.errors)
          .flatMap(([, msgs]) => (Array.isArray(msgs) ? msgs : [msgs]))
          .join(' ')
        : null;

      const msg =
        validationMsgs ||
        data?.message ||
        (resp?.status === 401 ? 'Unauthorized' :
          resp?.status === 403 ? 'Forbidden' :
            resp?.status === 422 ? 'Unprocessable request' :
              'Failed to create payment order');

      const details = data?.upstream ? ` Upstream: ${JSON.stringify(data.upstream)}` : '';
      const statusStr = resp?.status ? ` (HTTP ${resp.status})` : '';

      throw new Error(`${msg}${statusStr}${details}`);
    }
  }

  async retrieveSuccessToken(data) {
    this._ensureReady();
    const res = await this.api.post('/ezee/custom-token', data);
    return res.data;
  }

  async fetchPaymentHistory({ page = 1, take = 50, status, from, to } = {}) {
    this._ensureReady();

    // Build query string in a PHP/Laravel-friendly way
    const qp = new URLSearchParams();
    qp.set('page', String(page));
    qp.set('take', String(take));
    if (Array.isArray(status) && status.length > 0) {
      for (const s of status) qp.append('status[]', s); // Laravel validates status as array
    }
    if (from) qp.set('from', from); // yyyy-mm-dd or ISO-8601
    if (to) qp.set('to', to);

    const res = await this.api.get(`/payment/history?${qp.toString()}`);
    // { data, meta: { current_page, per_page, total, last_page } }
    return res.data;
  }

  async getUsageSummary() {
    this._ensureReady();
    const res = await this.api.get('/usages/summary');
    return res.data;
  }

}

export const apiService = new ExternalApiService();
